import React from 'react';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Edit from '@material-ui/icons/Edit';
import TextInput from '../TextInput';
import DefaultButton from '../Buttons/DefaultButton/button';
import {
   MobileNumberDetails,
   MobileNumberValidationDetails,
   getFieldClassName,
   getResultForMobileNumber,
   getWarningMessages,
   MOBILE_NUMBER_DUPLICATE_EDIT_ACTION,
} from './types';
import MobileNumberWarning from './MobileNumberWarning';

export const EditingStatusChip = () => (
   <Chip
      className="verification-status-chip verification-status-chip-editing"
      icon={<Edit className="verification-status-chip-icon" />}
      label="Editing"
   />
);

interface MobileNumberEditProps {
   mobileNumberDetails: MobileNumberDetails;
   initialMobileNumber: string;
   validationDetails?: MobileNumberValidationDetails;
   isValidatingMobileNumber?: boolean;
   sendOtpData?: MobileNumberValidationDetails;
   isSendingOtp?: boolean;
   inputValid?: boolean;
   onMobileNumberChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
   onSave: () => void;
   onCancel: () => void;
}

const MobileNumberEdit = ({
   mobileNumberDetails,
   initialMobileNumber,
   validationDetails,
   isValidatingMobileNumber = false,
   sendOtpData,
   isSendingOtp = false,
   inputValid,
   onMobileNumberChange,
   onSave,
   onCancel,
}: MobileNumberEditProps) => {
   // Both results are read through the number they were produced for: the store holds one
   // validateMobileNumberResult slot, also written by the read-only view's on-load check,
   // and a debounced check can land after the input has moved on.
   const typedNumber = mobileNumberDetails.mobileNumber;
   const validation = getResultForMobileNumber(validationDetails, typedNumber);
   const sendOtpResult = getResultForMobileNumber(sendOtpData, typedNumber);

   const validationError =
      validation && !validation.isSuccess ? validation.errorMessage : null;

   const isValidationSuccess = !!(validation && validation.isSuccess);

   // Nothing has been typed or validated yet on the first render, and TextInput treats
   // any falsy inputValid as invalid - so the number is only marked bad once the
   // validation has actually run against this number and rejected it.
   const isNumberMarkedInvalid = validation ? isValidationSuccess : true;

   const sendOtpError =
      sendOtpResult && !sendOtpResult.isSuccess
         ? sendOtpResult.errorMessage
         : null;

   return (
      <div className={getFieldClassName(inputValid)}>
         <div className="mobile-number-text-field-header">
            <span className="grey2-text input-header">
               Contact Mobile Number <span className="text-red">*</span>
            </span>
            <EditingStatusChip />
         </div>
         <div className="mobile-number-text-field-input">
            <TextInput
               type="tel"
               id="mobile_number_text_field"
               placeholder="Your mobile number"
               value={mobileNumberDetails.mobileNumber || ''}
               inputValid={isNumberMarkedInvalid}
               onChange={onMobileNumberChange}
               onSubmit={onSave}
               mandatory
            />
            {isValidatingMobileNumber && (
               <CircularProgress
                  className="mobile-number-text-field-spinner"
                  size={18}
                  thickness={4}
               />
            )}
         </div>

         {validationError && (
            <MobileNumberWarning
               messages={getWarningMessages(
                  validationError,
                  MOBILE_NUMBER_DUPLICATE_EDIT_ACTION
               )}
            />
         )}

         {sendOtpError && (
            <MobileNumberWarning
               messages={getWarningMessages(
                  sendOtpError,
                  MOBILE_NUMBER_DUPLICATE_EDIT_ACTION
               )}
            />
         )}

         {!isValidatingMobileNumber && isValidationSuccess && (
            <span className="mobile-number-success">
               Looks good, we'll text a code to confirm.
            </span>
         )}
         <div className="mobile-number-text-field-actions">
            {isSendingOtp && (
               <CircularProgress
                  className="mobile-number-send-code-spinner"
                  size={18}
                  thickness={4}
               />
            )}
            <span className="mobile-number-send-code-btn">
               <DefaultButton
                  className="btn"
                  type="primary"
                  text="Send Code"
                  onClick={onSave}
                  disabled={
                     isValidatingMobileNumber ||
                     isSendingOtp ||
                     !isValidationSuccess
                  }
               />
            </span>

            <DefaultButton
               className="btn"
               type="secondary"
               text="Cancel"
               onClick={onCancel}
            />
         </div>
      </div>
   );
};

export default MobileNumberEdit;
